import { Component, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ProjectFiltersComponent } from '../../../filters/project-filters/project-filters.component';
import { ToastrHelper } from '../../../helpers/toastr.helper';
import { Community } from '../../../models/identity/community.model';
import { ProjectFilters } from '../../../models/projects/project-filters.model';
import { Project } from '../../../models/projects/project.model';
import { AccountsService } from '../../../services/identity/accounts.service';
import { CommunityService } from '../../../services/identity/community.service';
import { ProjectsService } from '../../../services/projects/projects.service';
import { PwaService } from '../../../services/pwa/pwa.service';
import { DataStorageService } from '../../../services/shared/data-storage.service';
import { PermissionsService } from '../../../services/shared/permissions.service';

@Component({
    selector: 'app-projects-list',
    templateUrl: './projects-list.component.html',
    styleUrls: ['./projects-list.component.scss'],
})
export class ProjectsListComponent implements OnInit {
    @ViewChild('projectFilters', { static: false })
    private filtersComponent: ProjectFiltersComponent;

    public allCommunities: Community[];
    public applicationCode = environment.settings.code;
    public areFiltersOpened: boolean;
    public canSeeAllCommunitiesFilter = true;
    public canSeeOrganizationSkillsFilter = false;
    public dictionary = environment.dictionary;
    public isInitiated = false;
    public isLoaded = false;
    public loadProjectsSubscription: Subscription;
    public nextPage: boolean;
    public page = 1;
    public projects: Project[];
    public projectsLoaded: boolean;
    public projectCustomSearch = '';

    searchTextChanged = new Subject<string>();
    canCreateProject: boolean;

    constructor(
        private readonly toastrHelper: ToastrHelper,
        private readonly accountService: AccountsService,
        private readonly projectsService: ProjectsService,
        private readonly communityService: CommunityService,
        private readonly permissionsService: PermissionsService,
        public readonly dataStorage: DataStorageService,
        public pwaService: PwaService,
    ) {}

    public get isOnMobile(): boolean {
        return this.pwaService.isMobile && !this.pwaService.isTablet;
    }

    public get savedFilters(): ProjectFilters {
        return this.dataStorage.projectsFilters;
    }

    public set savedFilters(value: ProjectFilters) {
        this.dataStorage.projectsFilters = value;
    }

    public ngOnInit() {
        this.communityService.getCommunities().subscribe((communities) => {
            this.allCommunities = communities;
            this.isLoaded = true;
        });

        this.canSeeOrganizationSkillsFilter = this.accountService.isOrganizationManager();

        this.canSeeAllCommunitiesFilter = this.permissionsService.canSeeAllCommunities();

        this.searchTextChanged.pipe(debounceTime(500)).subscribe((textSearch) => this.handleSearchTextChange(textSearch));

        const isManagingCommunityOrAdmin =
            this.accountService.isUserManagingCommunity(this.accountService.currentUser.primaryCommunity) || this.accountService.isAdmin();

        this.canCreateProject = this.accountService.currentUser.primaryCommunity.canCreateProject || isManagingCommunityOrAdmin;
    }

    onProjectSearchTextChanged(textSearch: string): void {
        this.searchTextChanged.next(textSearch.trim());
    }

    private handleSearchTextChange(textSearch: string): void {
        this.projectCustomSearch = textSearch;
        this.loadProjects(false);
    }

    public loadFilters(): ProjectFilters {
        let filters;
        if (this.savedFilters) {
            filters = this.savedFilters;
            this.filtersComponent.setSelectedFilters(filters);
        } else {
            filters = this.filtersComponent.extractSelectedFilters();
            this.savedFilters = filters;
        }

        filters.page = this.page;
        filters.organizationId = filters.isMyOrganizationChecked ? this.accountService.currentUser.organization?.id : null;
        filters.communitiesIds = filters.isAllMyCommunitiesChecked
            ? this.accountService.currentUser.communities.map((community) => community.id)
            : filters.communitiesIds;
        filters.skillsIds = filters.isMySkillsChecked ? this.accountService.currentUser.skills.map((skill) => skill.id) : [];

        if (this.canSeeOrganizationSkillsFilter) {
            filters.organizationSkillsIds = filters.isMyOrganizationSkillsChecked
                ? this.accountService.currentUser.organization.skills?.map((skill) => skill.id)
                : null;
        }

        filters.interestsIds = filters.isMyInterestsChecked ? this.accountService.currentUser.interests.map((interest) => interest.id) : [];
        filters.search = this.projectCustomSearch;
        filters.projectParticipation = this.filtersComponent.getProjectParticipation();
        return filters;
    }

    public loadProjects(isPushMode: boolean) {
        if (!isPushMode) {
            this.page = 1;
            this.projects = [];
        }

        if (this.loadProjectsSubscription && !this.projectsLoaded) {
            this.loadProjectsSubscription.unsubscribe();
        }

        this.projectsLoaded = false;
        this.loadProjectsSubscription = this.projectsService.getFilteredProjects(this.loadFilters()).subscribe(
            (data) => {
                this.nextPage = data.length > 0;
                data.forEach((project) => this.projects.push(project));
            },
            (error) => {
                this.toastrHelper.showGenericError(error);
            },
            () => {
                this.projectsLoaded = true;
                this.isInitiated = true;
            },
        );
    }

    public onFiltersClosed() {
        this.areFiltersOpened = false;
    }

    public onFiltersReady() {
        this.nextPage = false;
        this.loadProjects(false);
    }

    public onFiltersSelectionChanged() {
        this.savedFilters = null;
        this.nextPage = false;
        this.loadProjects(false);
        window.scroll(0, 0);
    }

    public onScroll() {
        if (!this.nextPage || !this.projectsLoaded) {
            return;
        }

        this.page++;
        this.loadProjects(true);
    }

    public openFilters() {
        this.areFiltersOpened = true;
    }
}
